import { z } from '@hono/zod-openapi'

export const AuditEnvSchema = z.enum(['client', 'server', 'worker']).openapi({
  example: 'server',
  description: 'Layer that produced the audit entry',
})

export const AuditEntrySchema = z.object({
  id: z.string().uuid().openapi({ example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' }),
  districtId: z.string().min(1).max(64).openapi({ example: 'dist-wa-12345' }),
  studentId: z.string().uuid().nullable().openapi({ example: 'stu-5678-9012' }),
  actorId: z.string().min(1).max(255).openapi({ example: 'user-admin-01' }),
  action: z.string().min(1).max(128).openapi({ example: 'document.created' }),
  resource: z.string().max(255).nullable().openapi({ example: 'district/dist-wa/student/stu-5678/iep' }),
  details: z.record(z.unknown()).nullable(),
  timestamp: z.string().datetime().openapi({ example: '2026-07-15T12:00:00Z' }),
  checksum: z.string().length(64).openapi({ example: 'abc123def456...' }),
  env: AuditEnvSchema,
  seq: z.number().int().min(1).openapi({ example: 42 }),
}).openapi('AuditEntry', { description: 'A single entry in the audit hash chain' })

export const AuditAppendRequestSchema = z.object({
  districtId: z.string().min(1).max(64),
  studentId: z.string().uuid().nullable(),
  actorId: z.string().min(1).max(255),
  action: z.string().min(1).max(128),
  resource: z.string().max(255).nullable(),
  details: z.record(z.unknown()).nullable(),
  timestamp: z.string().datetime(),
  checksum: z.string().length(64),
  env: AuditEnvSchema,
  seq: z.number().int().min(1),
}).openapi('AuditAppendRequest')

export const AuditAppendResponseSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int(),
  verified: z.boolean(),
}).openapi('AuditAppendResponse')

export const AuditBatchRequestSchema = z.object({
  entries: z.array(AuditAppendRequestSchema).min(1).max(100),
}).openapi('AuditBatchRequest')

export const AuditBatchResponseSchema = z.object({
  accepted: z.number().int(),
  rejected: z.number().int(),
  errors: z.array(z.object({ index: z.number().int(), reason: z.string() })),
}).openapi('AuditBatchResponse')

export const AuditAnchorResponseSchema = z.object({
  anchorId: z.string().uuid(),
  headHash: z.string().length(64),
  depth: z.number().int(),
  verified: z.boolean(),
  lastVerifiedAt: z.string().datetime(),
}).openapi('AuditAnchorResponse')

export type AuditEntry = z.infer<typeof AuditEntrySchema>
export type AuditAppendRequest = z.infer<typeof AuditAppendRequestSchema>
export type AuditAppendResponse = z.infer<typeof AuditAppendResponseSchema>
export type AuditBatchRequest = z.infer<typeof AuditBatchRequestSchema>
export type AuditBatchResponse = z.infer<typeof AuditBatchResponseSchema>
export type AuditAnchorResponse = z.infer<typeof AuditAnchorResponseSchema>

export const GENESIS_ANCHOR_PREFIX = 'GENESIS_ANCHOR_'

export function makeGenesisAnchor(districtId: string): string {
  return `${GENESIS_ANCHOR_PREFIX}${districtId}`
}
